/**
 * utils/contactImportGroup.js
 * Contacts xlsx import, batch 3b of 5 (the FIRM GROUPING LAYER): proposes which shops resolved
 * by `contactImportResolve.js` (batch 3a) — from EITHER sheet, or several from the SAME sheet —
 * look like the same real-world firm. No React, no UI, no writes — this module NEVER creates a
 * `Business`, never sets `businessId`, and never merges/edits a plan row. Batch 5 is what shows a
 * human these proposals and turns a CONFIRMED one into a real business (see "WHAT THIS LEAVES
 * FOR BATCH 5" at the bottom).
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────────────────────
 * The same real business is often registered TWICE in the source file: at PUSAT under the
 * OWNER'S PERSONAL NAME with the shop name in brackets ("HARRY SOEN (ABADI MOTOR)"), and at
 * CABANG under the shop name with a city suffix ("ABADI MOTOR - SMD"). Measured on the real
 * file, ZERO shop names match exactly across the two sheets — plain name matching finds nothing.
 * A shared phone number is the strongest real signal (75 plausible pairs); a suffix-stripped
 * name match finds 16 more; containment is what catches the owner-name case. Missing a real pair
 * here leaves a firm silently split, with its combined outstanding total quietly wrong forever —
 * a wrong suggestion only ever costs a human one glance in batch 5. So every heuristic below is
 * deliberately loose, matching `contactMatch.js`'s own stated bias.
 *
 * ── REUSE, NOT A FORK ───────────────────────────────────────────────────────────────────────
 * Scoring is 100% `contactMatch.js`'s own `contactMatchScore({name, phone, address}, {...})` —
 * that module's own header already says it was kept decoupled from the live `Contact` shape
 * SPECIFICALLY so this bulk matcher could reuse it against parsed rows. The scoring RULES needed
 * no change at all: a resolved plan row already carries everything the descriptor wants, once
 * one real quirk is accounted for — **a plan row's `contactPerson` field holds a PHONE NUMBER,
 * not a person's name** (`contactImportParser.js`'s own header: "Contact Person" is a misleading
 * column header in the source file; it was never a name, on either sheet). `descriptorForRow`
 * below maps `contactPerson -> phone` for exactly this reason — this is the "phone" signal the
 * task's own measured 75-pair figure is about, not a separate field this file invents.
 * **One small, behavior-preserving PERFORMANCE extension WAS made to `contactMatch.js` itself**
 * (never forked): `contactMatchScore` used to recompute five string transforms (normalized name,
 * suffix-stripped name, condensed name, loose name, normalized phone) from scratch on every
 * single call. This bulk matcher calls it ~220,000 times over just 663 rows, reusing the SAME
 * descriptor object as an argument up to ~660 times each — that repeated recomputation was the
 * measured majority of this file's own wall-clock cost (see the SCALE section). `contactMatch.js`
 * now memoizes those five derived forms in a `WeakMap` keyed on the descriptor object itself, so
 * a descriptor reused across many calls only pays for the string work once. This changes nothing
 * about what any call returns — every test in `contactMatch.test.js` (all 26, plus its own
 * `suggestBusinessMatches` cases) passes unmodified — and a descriptor built fresh per call (as
 * `suggestBusinessMatches` itself already does today) costs nothing extra, since the cache entry
 * is simply never reused and is garbage-collected normally.
 *
 * ── SCALE — WHY THIS BRUTE-FORCES RATHER THAN PRE-FILTERING ────────────────────────────────
 * 663 shops (509 CABANG + 154 PUSAT, the real file's own counts) is ~219,453 unordered pairs.
 * This module scores every one of them — measured (not estimated) at **~5 seconds run alone,
 * ~11–12 seconds under the full suite's own parallel worker contention (`CI=true npm test`)** on
 * a synthetic 663-shop fixture shaped like the real file, in this project's own Jest/Babel test
 * environment (`contactImportGroup.test.js`'s own scale test prints the exact figure from that
 * run — read its console output for the current number rather than trusting this comment, which
 * will drift). The WeakMap memoization above only closed part of that gap (~5.4s -> ~5.0s alone);
 * most of the remaining cost is the Levenshtein edit-distance check itself (`similarityRatio`,
 * used only for NAME comparison — the address signal is a plain `===` on the whole normalized
 * string, see `contactMatch.js`'s own header for why a fuzzy address tier was tried and rejected
 * as a real-file regression), which is inherently PER-PAIR (two different strings) and cannot be
 * memoized per-row the way the other five derived forms were.
 * A pre-filter (bucket by shared phone, or by a shared name word/prefix) WAS considered to cut
 * pairs down further, and deliberately rejected: the single most important case this module
 * exists for — a PUSAT owner name containing a CABANG shop name, e.g. "HARRY SOEN (ABADI MOTOR)"
 * vs "ABADI MOTOR" — shares no common FIRST word or phone by construction (the owner's own first
 * name leads one side, the shop name leads the other). Any prefix/first-word bucket cheap enough
 * to matter would silently drop exactly that case, which is the one piece of "missing" this task
 * calls out by name as the failure that matters. A word-overlap index would catch it without that
 * risk, but was not built: this is a one-time, human-reviewed admin operation (the same class of
 * cost this app already accepts for `ImportWizard.js`'s own multi-thousand-item catalog commit,
 * deferred one tick via `setTimeout(...,0)` purely so a "Mengimpor…" state can paint first) — a
 * few seconds of synchronous compute, deferred the same way, is an acceptable, honestly-reported
 * cost against the alternative of a second class of recall risk. If the catalog ever grows by an
 * order of magnitude, that tradeoff should be re-measured, not assumed. **Batch 5's own wizard
 * step must defer the `groupProposedFirms` call the same way** (a bare synchronous call would
 * freeze the tab for several seconds with no feedback) — this module itself has no async/timing
 * concerns of its own, being pure, so that deferral is a UI-layer decision left to batch 5.
 *
 * ── GROUPING, NOT JUST PAIRING ──────────────────────────────────────────────────────────────
 * A matches B, B matches C, A does not match C: this module treats that as ONE group (a
 * connected component of the match graph — plain union-find), not two separate pairs. Reasoning:
 * under-grouping (leaving a real chain split into two disconnected suggestions) is exactly the
 * "silently wrong forever" failure this file exists to avoid, and a firm can legitimately have
 * several branches where each one resembles its neighbor more than it resembles the one furthest
 * away (three city-suffixed CABANG branches of one shop, say). The cost of choosing connected
 * components over strict cliques is that a group can contain a pair with NO direct edge between
 * them — this is never hidden: every group states `fullyConnected` (false the instant one such
 * pair exists) and every member's own `matches` array lists only its REAL, direct edges, so a
 * human always sees exactly which relationships are direct and which are "connected via someone
 * else" — never a group the human "cannot account for."
 * This also means a low-weight, noisy signal (address EXACT match — `contactMatch.js`'s own
 * "ranked last and noisiest on purpose" tier, and REQUIRES the whole normalized address, never a
 * prefix and never a fuzzy ratio, since a real 663-shop file measured dozens of unrelated shops
 * sharing just a street name — see that file's own header) can still chain shops together —
 * deliberately not filtered out of grouping, on the same "cast wide" bias as everything else here.
 * Its blast radius is self-limiting: a group's `strength` (see below) is its STRONGEST edge, so a group built only
 * from weak address-only edges reports a low strength and sorts near the bottom of the ranked
 * list — visible, never hidden, but never masquerading as a confident suggestion either.
 *
 * ── WHAT THIS PRODUCES ──────────────────────────────────────────────────────────────────────
 * `groupProposedFirms(shops) -> { groups, unmatched }`:
 * - `groups`: one entry per connected component of size >= 2, sorted by `strength` DESCENDING
 *   (ties broken by fewer members, then by the first member's name) — so the UI's default
 *   render order already puts the obvious matches first, per the task's own requirement.
 *   Each group: `{ members, strength, fullyConnected }`.
 *   - `members`: `[{ shop, matches }]` — `shop` is the ORIGINAL plan row (never cloned/edited),
 *     `matches` is that member's own list of `{ shop, score, reasons }` for every OTHER member it
 *     directly matched (both `score` and `reasons` come straight from `contactMatchScore`,
 *     including its own Indonesian reason strings, e.g. "Nomor telepon sama" — never reduced to
 *     a bare number, since the task is explicit that a score alone isn't judgeable in a second).
 *   - `strength`: the single highest `score` among any edge in the group — the one number the
 *     UI sorts/orders by.
 *   - `fullyConnected`: true only when EVERY pair of members has a direct edge (a real clique) —
 *     false the instant the group was formed by chaining through an intermediate member.
 * - `unmatched`: every input shop that landed in no group at all (score>0 against nobody) — the
 *   NORMAL case for most of the 663, per the task's own framing. Original order preserved.
 * Every plan row is accounted for exactly once, in either `groups` (as some member) or
 * `unmatched` — nothing is dropped.
 *
 * ── WHAT THIS LEAVES FOR BATCH 5 (nothing here is decided) ─────────────────────────────────
 * This layer only PROPOSES. For every group, a human must still: (1) confirm the group is real —
 * or edit it, e.g. drop a wrongly-included member such as a landline shared by an unrelated shop
 * (see the "shared landline" edge case in the test file); (2) decide the resulting `Business`'s
 * own name (never guessed here, same rule `BusinessLinkModal`'s own name step already follows —
 * see the root doc's Contact/Business entries); (3) actually call something that mints a
 * `Business` record and stamps `businessId` on each confirmed member's own eventual `Contact` —
 * this requires each member to already BE a contact, i.e. batch 5 runs this proposal step AFTER
 * (or alongside) `contactImportCommit.js`'s own commit, then links the newly-created contacts'
 * real ids the same way `App.js`'s `handleLinkContacts` already links any other contacts today.
 * This module knows nothing about `data`, ids, or `Business` at all — it only ever sees plan rows
 * (or, once committed, could just as well be re-run against real `Contact` records, since the
 * `{name, phone, address}` shape it reads is a subset of both).
 *
 * ── EDGE CASES (considered before writing this file) ───────────────────────────────────────
 * - A shop with no phone and a generic two-word name: scored exactly like any other pair via
 *   `contactMatchScore`'s own existing thresholds (nothing new here) — never crashes on a blank
 *   phone/address, and a same-tier-but-unrelated two-word name does not spuriously match unless
 *   `contactMatchScore` itself says it should (that threshold is that module's call, not this
 *   file's to loosen or tighten).
 * - Two unrelated shops sharing a phone (a shared landline): still forms a group, deliberately —
 *   `contactMatch.js`'s own header calls a shared phone the strongest signal, and this module
 *   does not second-guess it. Batch 5's human review is the actual safety net for the case where
 *   the landline is real but the shops genuinely aren't the same firm.
 * - A group spanning more than two entries from the SAME list: explicitly ALLOWED, not an error —
 *   several CABANG branches of one firm can match each other with no PUSAT counterpart in this
 *   batch at all (or several PUSAT rows for one ownership group). Nothing here requires exactly
 *   one PUSAT + one CABANG member.
 * - A shop matching many others weakly: forms one larger connected component rather than many
 *   separate pairs (see "GROUPING, NOT JUST PAIRING" above) — never silently dropped, but its
 *   `strength` reflects only its BEST edge, and every individual weak edge is still visible on
 *   its own member's `matches` list for a human to judge one at a time.
 * - Empty input: `{ groups: [], unmatched: [] }`, no crash.
 */

import { contactMatchScore } from "./contactMatch";

/** The `{name, phone, address}` descriptor `contactMatchScore` expects, read off an already-
 *  resolved plan row (`contactImportResolve.js`'s own plan shape). `phone` deliberately reads
 *  `contactPerson` — see this file's own header for why that is the real phone field, not a typo.
 *  `address` reads the shop's own first/primary address text (plan rows never mark one
 *  `isDefault` — see `contactImportResolve.js`'s own header — so "first" is the only real
 *  reading, same convention `contactMatch.js`'s own `descriptorFor` falls back to). */
function descriptorForRow(row) {
  const addr = row && Array.isArray(row.addresses) ? row.addresses[0] : null;
  return {
    name: row ? row.name : "",
    phone: row ? row.contactPerson : "",
    address: addr ? addr.text : "",
  };
}

function find(parent, x) {
  while (parent[x] !== x) {
    parent[x] = parent[parent[x]];
    x = parent[x];
  }
  return x;
}

function union(parent, a, b) {
  const ra = find(parent, a);
  const rb = find(parent, b);
  if (ra !== rb) parent[rb] = ra;
}

/**
 * Proposes firm groups across every resolved shop handed in — typically the full `plan` array
 * `contactImportResolve.js`'s `resolveContactImport` returns (both sheets already combined into
 * one flat array there; this module makes no CABANG/PUSAT distinction of its own). Pure,
 * deterministic, no mutation of `shops` or its rows.
 * @param {object[]} shops — plan rows (or any objects carrying `name`/`contactPerson`/`addresses`)
 * @returns {{ groups: { members: {shop:object, matches:{shop:object,score:number,reasons:string[]}[]}[], strength:number, fullyConnected:boolean }[], unmatched: object[] }}
 */
export function groupProposedFirms(shops) {
  const rows = Array.isArray(shops) ? shops : [];
  const n = rows.length;
  if (n === 0) return { groups: [], unmatched: [] };

  const descriptors = rows.map(descriptorForRow);
  const parent = Array.from({ length: n }, (_, i) => i);
  const edgesFor = Array.from({ length: n }, () => []);

  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const { score, reasons } = contactMatchScore(descriptors[i], descriptors[j]);
      if (score > 0) {
        edgesFor[i].push({ j, score, reasons });
        edgesFor[j].push({ j: i, score, reasons });
        union(parent, i, j);
      }
    }
  }

  const byRoot = new Map();
  for (let i = 0; i < n; i++) {
    const r = find(parent, i);
    if (!byRoot.has(r)) byRoot.set(r, []);
    byRoot.get(r).push(i);
  }

  const groups = [];
  const unmatched = [];
  for (const indices of byRoot.values()) {
    if (indices.length === 1) {
      unmatched.push(rows[indices[0]]);
      continue;
    }
    let strength = 0;
    let directedEdgeCount = 0;
    const members = indices.map((i) => {
      const matches = edgesFor[i].map((e) => {
        if (e.score > strength) strength = e.score;
        return { shop: rows[e.j], score: e.score, reasons: e.reasons };
      });
      directedEdgeCount += matches.length;
      return { shop: rows[i], matches };
    });
    const possiblePairs = (indices.length * (indices.length - 1)) / 2;
    const fullyConnected = directedEdgeCount / 2 === possiblePairs;
    groups.push({ members, strength, fullyConnected });
  }

  groups.sort((a, b) => {
    if (b.strength !== a.strength) return b.strength - a.strength;
    if (a.members.length !== b.members.length) return a.members.length - b.members.length;
    return String(a.members[0].shop.name).localeCompare(String(b.members[0].shop.name));
  });
  // `unmatched` is already in original input order by construction: a never-unioned index i is
  // always its own root, so `byRoot` first sees key `i` while the outer loop is AT i (Map
  // iteration order is insertion order) — no separate sort needed.

  return { groups, unmatched };
}
